/**
 * ExecutionLogTab — renders the GardenRun execution_log as a streaming event
 * feed with a progress bar, mirroring the styling from ExecutionLogPanel.tsx.
 *
 * Used as a tab in RunDetailView when execution_log entries are present.
 */
import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import type { GardenRun, GardenRunExecutionLogEntry } from '@/services/api';

interface Props {
  run: GardenRun;
}

const PHASE_COLORS: Record<string, string> = {
  init:           'bg-slate-100 text-slate-600',
  pass1_start:    'bg-blue-50 text-blue-600',
  pass2_start:    'bg-indigo-50 text-indigo-600',
  review:         'bg-teal-50 text-teal-700',
  complete:       'bg-green-50 text-green-700',
};

function PhaseBadge({ phase }: { phase: string }) {
  const cls = PHASE_COLORS[phase] ?? 'bg-slate-50 text-slate-500';
  return (
    <span className={clsx('text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded', cls)}>
      {phase.replace(/_/g, ' ')}
    </span>
  );
}

function LogRow({ entry }: { entry: GardenRunExecutionLogEntry }) {
  const ts = new Date(entry.timestamp);
  const timeStr = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const isError = entry.insight.startsWith('FAILED');
  return (
    <div
      className={clsx(
        'flex gap-3 px-3 py-2 rounded-lg border text-xs',
        isError
          ? 'border-red-100 bg-red-50'
          : 'border-slate-100 bg-white',
      )}
    >
      <span className="text-slate-400 shrink-0 font-mono w-20">{timeStr}</span>
      <PhaseBadge phase={entry.phase} />
      <span className={clsx('flex-1 text-slate-700', isError && 'text-red-700 font-medium')}>
        {entry.insight}
      </span>
      {entry.progress !== undefined && (
        <span className="shrink-0 text-slate-400 tabular-nums">{entry.progress}%</span>
      )}
    </div>
  );
}

const RUNNING = new Set(['running', 'queued', 'pending']);

export function ExecutionLogTab({ run }: Props) {
  const log: GardenRunExecutionLogEntry[] = run.execution_log ?? [];
  const isRunning = RUNNING.has(run.status);
  const latestEntry = log[log.length - 1];
  const progress = latestEntry?.progress ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom (newest) when running
  useEffect(() => {
    if (isRunning && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [log.length, isRunning]);

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      {(isRunning || log.length > 0) && (
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{latestEntry?.insight ?? (isRunning ? 'Initializing…' : 'Complete')}</span>
            <span className="tabular-nums">{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-700',
                run.status === 'failed' ? 'bg-red-400' : 'bg-brand-teal',
              )}
              style={{ width: `${Math.max(progress, run.status === 'completed' ? 100 : 0)}%` }}
            />
          </div>
          {isRunning && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-blue-500">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-ping" />
              Streaming live updates…
            </div>
          )}
        </div>
      )}

      {/* Event feed */}
      <div ref={scrollRef} className="space-y-1 max-h-[500px] overflow-y-auto">
        {log.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            {isRunning
              ? 'Waiting for first log entry…'
              : 'No execution log for this run.'}
          </p>
        ) : (
          [...log].reverse().map((entry, i) => (
            <LogRow key={i} entry={entry} />
          ))
        )}
      </div>

      {/* Footer counts */}
      {log.length > 0 && (
        <div className="text-xs text-slate-400 text-right">
          {log.length} event{log.length !== 1 ? 's' : ''}
          {log.filter(e => e.insight.startsWith('FAILED')).length > 0 && (
            <span className="ml-2 text-red-500">
              · {log.filter(e => e.insight.startsWith('FAILED')).length} failure{log.filter(e => e.insight.startsWith('FAILED')).length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
